import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Field, NativeSelect } from "./common";
import { api, errMsg } from "../lib/api";

const VTYPES = ["Camion", "Furgone", "Pala meccanica", "Escavatore", "Gru", "Betoniera", "Piattaforma", "Attrezzatura"];

const withKeys = (list) => (list || []).map((it) => ({ ...it, _key: it._key || newKey() }));
const newKey = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const stripKeys = (list) => (list || []).filter((l) => l.name).map(({ name, expiry }) => ({ name, expiry: expiry || null }));

function useFormState(open, initial, empty) {
  const [f, setF] = useState(empty);
  useEffect(() => {
    if (!open) return;
    const next = { ...empty, ...(initial || {}) };
    if ("licenses" in empty) Object.assign(next, { licenses: withKeys(next.licenses), certificates: withKeys(next.certificates) });
    setF(next);
  }, [open, initial, empty]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  return [f, setF, set];
}

const cleanValue = (k, v, numeric) => {
  if (v === "") return null;
  return numeric.includes(k) ? Number(v || 0) : v;
};

const clean = (obj, numeric = []) => {
  const o = {};
  for (const [k, v] of Object.entries(obj)) o[k] = cleanValue(k, v, numeric);
  return o;
};

function FormShell({ open, onOpenChange, title, children, onSubmit, busy, testid }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-3xl max-h-[92vh] overflow-y-auto scrollbar-thin" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-5">
          {children}
          <DialogFooter className="gap-2 sticky bottom-0 bg-popover pt-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={busy} data-testid="form-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, children }) => (
  <div><div className="eyebrow mb-3 text-amber-400/90">{title}</div><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div></div>
);

function ExpiryList({ label, items, onChange, testid }) {
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="sm:col-span-2 lg:col-span-3 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground">{label}</span>
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { name: "", expiry: "", _key: newKey() }])} data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi</Button></div>
      {items.map((it, i) => (
        <div key={it._key} className="flex gap-2">
          <Input placeholder="Descrizione" value={it.name} onChange={(e) => upd(i, "name", e.target.value)} />
          <Input type="date" className="w-44" value={it.expiry || ""} onChange={(e) => upd(i, "expiry", e.target.value)} />
          <Button type="button" size="icon" variant="ghost" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}

async function uploadPhoto(kind, id, file) {
  if (!file) return;
  const fd = new FormData();
  fd.append("file", file);
  await api.post(`/${kind}/${id}/photo`, fd);
}

const W_EMPTY = { first_name: "", last_name: "", fiscal_code: "", birth_date: "", birth_place: "", nationality: "", address: "", phone: "", email: "", iban: "", hire_date: "",
  qualification: "", ccnl_level: "", hourly_gross: 0, hourly_company_cost: 0, dpi_size: "", licenses: [], certificates: [],
  medical_fitness: "", medical_expiry: "", disciplinary_notes: "", notes: "" };

function workerBody(f) {
  const body = clean(Object.fromEntries(Object.keys(W_EMPTY).map((k) => [k, f[k]])), ["hourly_gross", "hourly_company_cost"]);
  body.licenses = stripKeys(f.licenses);
  body.certificates = stripKeys(f.certificates);
  return body;
}

const NATIONALITIES = ["Italiana", "Rumena", "Albanese", "Marocchina", "Ucraina", "Moldava", "Tunisina", "Egiziana", "Senegalese",
  "Nigeriana", "Pakistana", "Bengalese", "Indiana", "Cinese", "Filippina", "Peruviana", "Ecuadoriana", "Macedone", "Serba", "Kosovara",
  "Bulgara", "Polacca", "Ghanese", "Brasiliana"];

function WorkerPersonal({ f, set, setPhoto }) {
  return (
    <Section title="Anagrafica">
      <Field label="Nome *"><Input required value={f.first_name} onChange={set("first_name")} data-testid="worker-first-name-input" /></Field>
      <Field label="Cognome *"><Input required value={f.last_name} onChange={set("last_name")} data-testid="worker-last-name-input" /></Field>
      <Field label="Codice fiscale"><Input value={f.fiscal_code || ""} onChange={set("fiscal_code")} data-testid="worker-cf-input" /></Field>
      <Field label="Data di nascita"><Input type="date" value={f.birth_date || ""} onChange={set("birth_date")} /></Field>
      <Field label="Luogo di nascita"><Input value={f.birth_place || ""} onChange={set("birth_place")} placeholder="Comune (Provincia) o Stato estero" data-testid="worker-birth-place-input" /></Field>
      <Field label="Nazionalità"><Input list="nationality-options" value={f.nationality || ""} onChange={set("nationality")} data-testid="worker-nationality-input" />
        <datalist id="nationality-options">{NATIONALITIES.map((n) => <option key={n} value={n} />)}</datalist></Field>
      <Field label="Indirizzo" className="lg:col-span-2"><Input value={f.address || ""} onChange={set("address")} /></Field>
      <Field label="Cellulare"><Input value={f.phone || ""} onChange={set("phone")} data-testid="worker-phone-input" /></Field>
      <Field label="Email"><Input type="email" value={f.email || ""} onChange={set("email")} /></Field>
      <Field label="IBAN"><Input value={f.iban || ""} onChange={set("iban")} /></Field>
      <Field label="Foto"><Input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files[0])} data-testid="worker-photo-input" /></Field>
    </Section>
  );
}

function WorkerContract({ f, set, quals }) {
  const legacy = f.qualification && !quals.includes(f.qualification);
  return (
    <Section title="Contratto e costi">
      <Field label="Data assunzione"><Input type="date" value={f.hire_date || ""} onChange={set("hire_date")} /></Field>
      <Field label="Mansione *"><NativeSelect required value={f.qualification || ""} onChange={set("qualification")} data-testid="worker-qualification-select">
        <option value="" disabled>— Seleziona —</option>
        {legacy && <option>{f.qualification}</option>}
        {quals.map((q) => <option key={q}>{q}</option>)}
      </NativeSelect></Field>
      <Field label="Livello CCNL"><Input value={f.ccnl_level || ""} onChange={set("ccnl_level")} /></Field>
      <Field label="Costo orario lordo €"><Input type="number" step="0.01" value={f.hourly_gross} onChange={set("hourly_gross")} /></Field>
      <Field label="Costo aziendale orario € *"><Input type="number" step="0.01" required value={f.hourly_company_cost} onChange={set("hourly_company_cost")} data-testid="worker-cost-input" /></Field>
      <Field label="Taglia DPI"><Input value={f.dpi_size || ""} onChange={set("dpi_size")} /></Field>
    </Section>
  );
}

function WorkerQualifications({ f, set, setF }) {
  return (
    <Section title="Qualifiche, attestati e idoneità">
      <ExpiryList label="Patentini (con scadenza)" items={f.licenses || []} onChange={(v) => setF((p) => ({ ...p, licenses: v }))} testid="worker-licenses" />
      <ExpiryList label="Attestati (con scadenza)" items={f.certificates || []} onChange={(v) => setF((p) => ({ ...p, certificates: v }))} testid="worker-certificates" />
      <Field label="Idoneità medica"><Input value={f.medical_fitness || ""} onChange={set("medical_fitness")} /></Field>
      <Field label="Scadenza idoneità"><Input type="date" value={f.medical_expiry || ""} onChange={set("medical_expiry")} /></Field>
    </Section>
  );
}

export function WorkerForm({ open, onOpenChange, worker, onSaved }) {
  const [f, setF, set] = useFormState(open, worker, W_EMPTY);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quals, setQuals] = useState([]);
  useEffect(() => { if (open) api.get("/qualifications").then((r) => setQuals(r.data.items.map((q) => q.name))); }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      const body = workerBody(f);
      const r = worker?.id ? await api.put(`/workers/${worker.id}`, body) : await api.post("/workers", body);
      await uploadPhoto("workers", r.data.id, photo);
      toast.success("✓ Operaio salvato");
      setPhoto(null);
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(`✕ ${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormShell open={open} onOpenChange={onOpenChange} title={worker?.id ? "Modifica operaio" : "Nuovo operaio"} onSubmit={submit} busy={busy} testid="worker-form">
      <WorkerPersonal f={f} set={set} setPhoto={setPhoto} />
      <WorkerContract f={f} set={set} quals={quals} />
      <WorkerQualifications f={f} set={set} setF={setF} />
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Note disciplinari"><Textarea value={f.disciplinary_notes || ""} onChange={set("disciplinary_notes")} /></Field>
        <Field label="Note generali"><Textarea value={f.notes || ""} onChange={set("notes")} /></Field>
      </div>
    </FormShell>
  );
}

const S_EMPTY = { code: "", name: "", client: "", site_type: "", address: "", latitude: "", longitude: "", start_date: "", end_planned: "", end_actual: "",
  status: "da_avviare", manager_id: "", budget: 0, notes: "" };

function SiteGeneral({ f, set, types }) {
  const legacy = f.site_type && !types.includes(f.site_type);
  return (
    <Section title="Dati generali">
      <Field label="Codice *"><Input required value={f.code} onChange={set("code")} data-testid="site-code-input" placeholder="CANT-007" /></Field>
      <Field label="Nome *" className="lg:col-span-2"><Input required value={f.name} onChange={set("name")} data-testid="site-name-input" /></Field>
      <Field label="Cliente / Committente"><Input value={f.client || ""} onChange={set("client")} data-testid="site-client-input" /></Field>
      <Field label="Tipologia"><NativeSelect value={f.site_type || ""} onChange={set("site_type")} data-testid="site-type-select">
        <option value="">— Nessuna —</option>
        {legacy && <option>{f.site_type}</option>}
        {types.map((t) => <option key={t}>{t}</option>)}
      </NativeSelect></Field>
      <Field label="Indirizzo" className="lg:col-span-2"><Input value={f.address || ""} onChange={set("address")} data-testid="site-address-input" /></Field>
      <Field label="Latitudine"><Input type="number" step="any" value={f.latitude ?? ""} onChange={set("latitude")} /></Field>
      <Field label="Longitudine"><Input type="number" step="any" value={f.longitude ?? ""} onChange={set("longitude")} /></Field>
      <Field label="Stato"><NativeSelect value={f.status} onChange={set("status")} data-testid="site-status-select">
        <option value="da_avviare">Da Avviare</option><option value="attivo">Attivo</option><option value="sospeso">Sospeso</option><option value="chiuso">Chiuso</option>
      </NativeSelect></Field>
    </Section>
  );
}

function SiteSchedule({ f, set, managers }) {
  return (
    <Section title="Tempi, responsabile e budget">
      <Field label="Data inizio"><Input type="date" value={f.start_date || ""} onChange={set("start_date")} data-testid="site-start-input" /></Field>
      <Field label="Fine prevista"><Input type="date" value={f.end_planned || ""} onChange={set("end_planned")} data-testid="site-end-input" /></Field>
      <Field label="Fine effettiva"><Input type="date" value={f.end_actual || ""} onChange={set("end_actual")} /></Field>
      <Field label="Responsabile"><NativeSelect value={f.manager_id ?? ""} onChange={set("manager_id")} data-testid="site-manager-select">
        <option value="">— Nessuno —</option>{managers.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
      </NativeSelect></Field>
      <Field label={f.budget_from_quote ? "Budget € (dal Valore cantiere)" : "Budget € (Valore cantiere finché manca il preventivo)"}>
        <Input type="number" value={f.budget} onChange={set("budget")} disabled={Boolean(f.budget_from_quote)} data-testid="site-budget-input" />
        {f.budget_from_quote && <div className="text-[11px] text-muted-foreground mt-1" data-testid="site-budget-locked-hint">Si aggiorna da solo con preventivo e varianti approvate in Economia.</div>}
      </Field>
    </Section>
  );
}

function useOptions(open, url, map) {
  const [items, setItems] = useState([]);
  useEffect(() => { if (open) api.get(url).then((r) => setItems(map(r.data.items))); }, [open, url, map]);
  return items;
}

const typeNames = (items) => items.map((t) => t.name);
const identity = (items) => items;

function siteBody(f) {
  const body = clean(Object.fromEntries(Object.keys(S_EMPTY).map((k) => [k, f[k] ?? ""])), ["budget"]);
  for (const k of ["latitude", "longitude", "manager_id"]) body[k] = body[k] === null ? null : Number(body[k]);
  return body;
}

export function SiteForm({ open, onOpenChange, site, onSaved }) {
  const [f, , set] = useFormState(open, site, S_EMPTY);
  const managers = useOptions(open, "/users/managers", identity);
  const types = useOptions(open, "/site-types", typeNames);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = siteBody(f);
      const r = site?.id ? await api.put(`/sites/${site.id}`, body) : await api.post("/sites", body);
      toast.success(site?.id ? "✓ Cantiere aggiornato" : "✓ Cantiere creato");
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(`✕ ${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormShell open={open} onOpenChange={onOpenChange} title={site?.id ? "Modifica cantiere" : "Nuovo cantiere"} onSubmit={submit} busy={busy} testid="site-form">
      <SiteGeneral f={f} set={set} types={types} />
      <SiteSchedule f={f} set={set} managers={managers} />
      <Field label="Note"><Textarea value={f.notes || ""} onChange={set("notes")} /></Field>
    </FormShell>
  );
}

const V_EMPTY = { type: "Furgone", brand: "", model: "", plate: "", revision_date: "", insurance_date: "", tax_date: "", daily_cost: 0, maintenance: false, notes: "" };

export function VehicleForm({ open, onOpenChange, vehicle, onSaved }) {
  const [f, setF, set] = useFormState(open, vehicle, V_EMPTY);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = clean(Object.fromEntries(Object.keys(V_EMPTY).map((k) => [k, f[k] ?? ""])), ["daily_cost"]);
      body.maintenance = !!f.maintenance;
      const r = vehicle?.id ? await api.put(`/vehicles/${vehicle.id}`, body) : await api.post("/vehicles", body);
      await uploadPhoto("vehicles", r.data.id, photo);
      toast.success("✓ Mezzo salvato");
      setPhoto(null);
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(`✕ ${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormShell open={open} onOpenChange={onOpenChange} title={vehicle?.id ? "Modifica mezzo" : "Nuovo mezzo"} onSubmit={submit} busy={busy} testid="vehicle-form">
      <Section title="Dati mezzo">
        <Field label="Tipo *"><NativeSelect value={f.type} onChange={set("type")} data-testid="vehicle-type-select">{VTYPES.map((t) => <option key={t}>{t}</option>)}</NativeSelect></Field>
        <Field label="Marca *"><Input required value={f.brand} onChange={set("brand")} data-testid="vehicle-brand-input" /></Field>
        <Field label="Modello *"><Input required value={f.model} onChange={set("model")} data-testid="vehicle-model-input" /></Field>
        <Field label="Targa / Matricola *"><Input required value={f.plate} onChange={set("plate")} data-testid="vehicle-plate-input" /></Field>
        <Field label="Costo giornaliero € *"><Input type="number" step="0.01" required value={f.daily_cost} onChange={set("daily_cost")} data-testid="vehicle-cost-input" /></Field>
        <Field label="Foto"><Input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files[0])} /></Field>
      </Section>
      <Section title="Scadenze e stato">
        <Field label="Revisione"><Input type="date" value={f.revision_date || ""} onChange={set("revision_date")} /></Field>
        <Field label="Assicurazione"><Input type="date" value={f.insurance_date || ""} onChange={set("insurance_date")} /></Field>
        <Field label="Bollo"><Input type="date" value={f.tax_date || ""} onChange={set("tax_date")} /></Field>
        <div className="flex items-center gap-3 pt-6"><Switch checked={!!f.maintenance} onCheckedChange={(v) => setF((p) => ({ ...p, maintenance: v }))} data-testid="vehicle-maintenance-switch" /><span className="text-sm">In manutenzione</span></div>
      </Section>
      <Field label="Note"><Textarea value={f.notes || ""} onChange={set("notes")} /></Field>
    </FormShell>
  );
}
